import { useCallback, useEffect, useRef, useState } from 'react'
import { aiInsightsApi } from '../api/aiInsights'
import type { AiInsight, InsightRunSummary, Lens } from '../types'

interface UseAiInsightsResult {
  insights: AiInsight[]
  /** All runs (newest-first), any status. Capped at 50 server-side. */
  runs: InsightRunSummary[]
  /** Convenience: runs filtered to status='running'. */
  runningRuns: InsightRunSummary[]
  loading: boolean
  /** True when at least one run is in flight. Driven by `runs`, not by
   *  whether the user can press Generate (Generate is always allowed). */
  refreshing: boolean
  error: Error | null
  refresh: () => Promise<void>
  accept: (id: string) => Promise<void>
  skip: (id: string) => Promise<void>
}

const RUNS_POLL_MS = 2000
const LIST_POLL_MS = 4000

export function useAiInsights(caseId: string | null, lens: Lens = 'graph'): UseAiInsightsResult {
  const [insights, setInsights] = useState<AiInsight[]>([])
  const [runs, setRuns] = useState<InsightRunSummary[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)
  const runningRuns = runs.filter(r => r.status === 'running')
  const refreshing = runningRuns.length > 0
  const isMountedRef = useRef(true)

  useEffect(() => {
    isMountedRef.current = true
    return () => {
      isMountedRef.current = false
    }
  }, [])

  const loadList = useCallback(async () => {
    if (!caseId) return
    try {
      const next = await aiInsightsApi.list(caseId, lens, 'active')
      if (isMountedRef.current) setInsights(next)
    } catch (e) {
      if (isMountedRef.current) setError(e as Error)
    }
  }, [caseId, lens])

  const loadRuns = useCallback(async () => {
    if (!caseId) return
    try {
      // Pull all runs (newest first); the panel uses statuses to render
      // each row's chip and the running subset to drive the badge.
      const next = await aiInsightsApi.runs(caseId, 'all')
      if (isMountedRef.current) setRuns(next)
    } catch {
      // Runs endpoint failures are non-fatal — badge just won't show.
    }
  }, [caseId])

  // No auto-fetch on mount. AI insights are user-triggered: the page
  // shows a "Generate insights" CTA which calls `refresh()` and the
  // polling effect below picks up runs as they execute. Reset state
  // when the caseId or lens changes so a stale list doesn't bleed
  // across navigation.
  useEffect(() => {
    setInsights([])
    setRuns([])
    setError(null)
    setLoading(false)
  }, [caseId, lens])

  // Poll while at least one run is in flight.
  const runningCount = runningRuns.length
  useEffect(() => {
    if (!caseId || runningCount === 0) return
    const runsTick = window.setInterval(loadRuns, RUNS_POLL_MS)
    const listTick = window.setInterval(loadList, LIST_POLL_MS)
    return () => {
      window.clearInterval(runsTick)
      window.clearInterval(listTick)
    }
  }, [caseId, runningCount, loadList, loadRuns])

  // When the last in-flight run finishes, do one final list refresh so any
  // newly-emitted insights land immediately.
  const prevRunningRef = useRef(0)
  useEffect(() => {
    if (prevRunningRef.current > 0 && runningCount === 0) {
      void loadList()
    }
    prevRunningRef.current = runningCount
  }, [runningCount, loadList])

  const refresh = useCallback(async () => {
    if (!caseId) return
    try {
      await aiInsightsApi.refresh(caseId, lens)
      await loadRuns()
    } catch (e) {
      setError(e as Error)
    }
  }, [caseId, lens, loadRuns])

  const accept = useCallback(
    async (id: string) => {
      if (!caseId) return
      try {
        await aiInsightsApi.accept(caseId, id)
        // Optimistic local removal — accepted items leave the active queue.
        setInsights(prev => prev.filter(i => i.id !== id))
      } catch (e) {
        setError(e as Error)
        await loadList()
      }
    },
    [caseId, loadList]
  )

  const skip = useCallback(
    async (id: string) => {
      if (!caseId) return
      try {
        await aiInsightsApi.skip(caseId, id)
        setInsights(prev => prev.filter(i => i.id !== id))
      } catch (e) {
        setError(e as Error)
        await loadList()
      }
    },
    [caseId, loadList]
  )

  return { insights, runs, runningRuns, loading, refreshing, error, refresh, accept, skip }
}
